import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../LanguageContext';
import type { Bi } from '../../data/sectors';
import { overviewHero as c, LOGO_BCITI, LOGO_ATNA } from '../../data/platformOverview';
const EASE = [0.23, 1, 0.32, 1] as const;

// Simplified fragment of the architecture map shown on Screen 2.
const S = 460;
const C = S / 2;
const TOP_X = [70, 140, 200, 260, 320, 390];
const RING_DOTS: [number, number][] = [[150, 30], [150, 150], [150, 210], [196, -40], [196, 70], [196, 120], [196, 175], [196, 250]];
function ArchitectureFragment() {
  const pt = (r: number, a: number) => [C + r * Math.cos(a * Math.PI / 180), C + 40 + r * Math.sin(a * Math.PI / 180)];
  return <div className="relative mx-auto w-full max-w-[460px] aspect-square" aria-hidden="true" data-id="element-5627" data-mp="3fec8q2l0ryp5">
      <svg viewBox={`0 0 ${S} ${S}`} className="absolute inset-0 w-full h-full" data-id="element-5628" data-mp="3jhqqoysiqp58">
        <defs data-id="element-5629" data-mp="3ofsh64s9xqqq">
          <radialGradient id="po-hero-halo" cx="50%" cy="50%" r="50%" data-id="element-5630" data-mp="3ofah0x2hqsdj">
            <stop offset="0%" stopColor="#EEEDFD" data-id="element-5631" data-mp="1usmcsgsoott5" />
            <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" data-id="element-5632" data-mp="1uu5wkhdjtmzo" />
          </radialGradient>
          <linearGradient id="po-hero-fade" x1="0" y1="0" x2="0" y2="1" data-id="element-5633" data-mp="1qq7txwp0lu1v">
            <stop offset="70%" stopColor="#FFFFFF" stopOpacity="0" data-id="element-5634" data-mp="1lr4rmk0f61xl" />
            <stop offset="100%" stopColor="#FFFFFF" data-id="element-5635" data-mp="1gsths9a4jrfd" />
          </linearGradient>
        </defs>
        <circle cx={C} cy={C + 40} r={220} fill="url(#po-hero-halo)" data-id="element-5636" data-mp="1gv6571ynitzt" />
        <circle cx={C} cy={C + 40} r={196} fill="none" stroke="#E6E5F3" strokeDasharray="2 7" data-id="element-5637" data-mp="2efsh6rs7bdgy" />
        <circle cx={C} cy={C + 40} r={150} fill="none" stroke="#DCDAF5" strokeDasharray="3 6" data-id="element-5638" data-mp="29i0q1zzdrvdt" />
        <circle cx={C} cy={C + 40} r={104} fill="#F4F3FE" data-id="element-5639" data-mp="24p7n9hnh2x4d" />
        <circle cx={C} cy={C + 40} r={96} fill="none" stroke="#D5D2F6" data-id="element-5640" data-mp="1zs7h76qmhud9" />
        {TOP_X.map(x => <path key={x} d={`M ${x} 22 C ${x} 70, ${C + (x - C) * 0.12} 70, ${C + (x - C) * 0.05} ${C + 40 - 104}`} fill="none" stroke="#BEB9F3" strokeWidth={1.1} data-id="element-5641" data-mp="2x7l72dbz4ikg" />)}
        {TOP_X.map(x => <rect key={`n${x}`} x={x - 9} y={4} width={18} height={18} rx={5} fill="#FFFFFF" stroke="#D8D5F7" data-id="element-5642" data-mp="qikpnwkpvf5h" />)}
        {RING_DOTS.map(([r, a]) => {
        const [x, y] = pt(r, a);
        return <circle key={`${r}-${a}`} cx={x} cy={y} r={r === 150 ? 4 : 3} fill={r === 150 ? '#4F46E5' : '#FFFFFF'} stroke={r === 150 ? 'none' : '#A7ACBE'} data-id="element-5643" data-mp="gtsz9svb12wo" />;
      })}
        {[-126, -54, 30, 90, 150].map(a => {
        const [x, y] = pt(96, a);
        return <circle key={a} cx={x} cy={y} r={6} fill="#FFFFFF" stroke="#B9B4F4" data-id="element-5644" data-mp="6x6ibf63mvzx" />;
      })}
        <rect x={0} y={0} width={S} height={S} fill="url(#po-hero-fade)" data-id="element-5645" data-mp="brtv4grqq1yo" />
      </svg>
      <div className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 w-[34%] aspect-square rounded-full bg-white border border-[#E2E0FA] flex flex-col items-center justify-center shadow-[0_20px_50px_-28px_rgba(79,70,229,0.55)]" style={{
      top: `${(C + 40) / S * 100}%`
    }} data-id="element-5646" data-mp="3u1dyjw2jvftb">
        <div className="flex items-center" data-id="element-5647" data-mp="1x5ybxiveooco">
          <img src={LOGO_BCITI} alt="" className="h-8 w-auto object-contain" data-id="element-5648" data-mp="1x4wzb9yqrtsu" />
          <span className="mx-0.5 text-[#121A3A]/25 font-light select-none" data-id="element-5649" data-mp="27bg1ode60lwm">|</span>
          <img src={LOGO_ATNA} alt="" className="h-7 w-auto object-contain" data-id="element-5650" data-mp="22doc3q0ztbvl" />
        </div>
      </div>
    </div>;
}
export function PlatformOverviewHero() {
  const {
    lang
  } = useLanguage();
  const pick = (b: Bi) => lang === 'FR' ? b.fr : b.en;
  return <section className="relative overflow-hidden bg-white px-6 pt-16 lg:pt-24 pb-16 lg:pb-20" aria-labelledby="platform-hero-title" data-id="element-5651" data-mp="2q167dvjgus3b">
      <div className="relative max-w-[1240px] mx-auto grid lg:grid-cols-[1.1fr_0.9fr] gap-12 xl:gap-20 items-center" data-id="element-5652" data-mp="2l1taehyvs4n0">
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: EASE
      }} data-id="element-5653" data-mp="2z1ktgx498b7v">
          <p className="font-sans text-[12.5px] font-semibold tracking-[0.28em] uppercase text-[#4F46E5]" data-id="element-5654" data-mp="14ocy94cd9xlp">{pick(c.eyebrow)}</p>
          <h1 id="platform-hero-title" className="mt-6 font-serif text-[2.5rem] sm:text-[3.2rem] lg:text-[3.6rem] xl:text-[4.1rem] leading-[1] tracking-tight" data-id="element-5655" data-mp="10nj9k8r3oerr">
            {c.headline.map(l => <span key={l.text.fr} className={`block ${l.accent ? 'text-[#4F46E5]' : 'text-[#121A3A]'}`} data-id="element-5656" data-mp="17289v7lyzobv">{pick(l.text)}</span>)}
          </h1>
          <p className="mt-7 max-w-[500px] font-sans text-[16px] md:text-[17px] text-[#4A5268] leading-relaxed" data-id="element-5657" data-mp="1b2s70b6jmry0">{pick(c.body)}</p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.97
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.3,
        delay: 0.08,
        ease: EASE
      }} className="max-w-[360px] lg:max-w-none mx-auto w-full" data-id="element-5658" data-mp="jego7dejhyxx">
          <ArchitectureFragment data-id="element-5659" data-mp="sdbt8m5ul2zn" />
        </motion.div>
      </div>
    </section>;
}